/**
 * Applying an intervention to the rep's book.
 *
 * One pure function does three jobs, which is why it is worth having: the page
 * calls it speculatively to *preview* what a lever would do before the rep
 * commits, calls it for real to *apply*, and calls it with the captured
 * before-state to *undo*. Three behaviours that must agree, from one
 * implementation.
 *
 * ## How a rep-set value changes the money
 *
 * The two numeric levers act on different halves of the same mechanism, and
 * keeping them distinct is what makes them explainable:
 *
 * - **Conversion override** replaces the curve's *size* — how much of the quote
 *   is still expected to convert — while leaving the curve's phasing alone.
 * - **Expected date** replaces the curve's *phasing* — when it lands — while
 *   leaving the size alone. A rep who has been given a real date is asserting a
 *   point, not a spread, so the whole remaining value lands on that date rather
 *   than being smeared across the curve's anchors.
 *
 * Together they let a rep say the complete thing: "£60k, three-quarters likely,
 * landing 3 September" — and September is where it shows up.
 *
 * ## Why the totals are recomputed here rather than refetched
 *
 * Journey-stage, there is no backend to refetch from. But the shape is also the
 * one the real page wants: the rep sees the effect of the lever in the same
 * frame they pull it, and the refetch that follows confirms rather than reveals.
 */

import type {
  ArrivalResolution,
  ExpectedConversion,
  MarkLostOutcome,
  MyPipelineResponse,
  PipelineClient,
  PipelineQuote,
  RepAttribution,
} from "../data/my-pipeline-types";
import {
  curveLandings,
  remainingConversion,
  type CurvePoint,
} from "./win-rate-curve";
import { monthKeyOf } from "./month-keys";

/**
 * The month a curve anchor lands in for this quote, from the backend's working
 * calendar (bank holidays known): the frontend never counts working days.
 */
function landingMonth(quote: PipelineQuote, anchorWd: number): string | null {
  return (
    quote.anchor_landing_months.find((m) => m.anchor_wd === anchorWd)?.month ??
    null
  );
}

/**
 * A manual move of this share of the rep's month projection alerts the sales
 * director (product brief §5, "Intervention log + alerts"). Exposed so the page
 * can warn the rep as they approach it — a control the person being controlled
 * cannot see is a trap, not a control.
 */
export const MATERIAL_MOVE_PCT = 0.1;

/** What the rep did. */
export type Intervention =
  | { kind: "mark_lost"; reasonCode: string; note: string }
  | { kind: "confirm_still_live"; note: string }
  | { kind: "set_conversion"; share: number; note: string }
  | { kind: "set_expected_date"; date: string; note: string }
  | { kind: "revert_conversion" }
  | { kind: "revert_expected_date" }
  | { kind: "set_unseen"; value: number; note: string }
  | { kind: "revert_unseen" }
  | {
      kind: "resolve_arrival";
      arrivalId: string;
      resolution: ArrivalResolution;
      note: string;
    };

/** What it was done to. */
export type InterventionTarget =
  | { kind: "quote"; clientId: string; quoteId: string }
  | { kind: "client"; clientId: string }
  | { kind: "arrival"; clientId: string; arrivalId: string };

/**
 * Which quotes a lever applies to.
 *
 * A quote's *reference* is the Tharstern HEADER ref, and one enquiry quoted as
 * several products shares it across several estimates — so resolving a target
 * by reference applied the lever, and its Tharstern write-back, to every
 * sibling at once (one gesture cleared five separate products under EST
 * 641116 on staging). Identity here is the quote id, never the label the rep
 * happens to see.
 *
 * A bulk gesture carries its own explicit selection and is honoured as given.
 */
export function resolveQuoteTargets(
  clients: readonly {
    id: string;
    quotes: readonly { id: string }[];
  }[],
  {
    quoteId,
    selectedIds,
  }: { quoteId?: string; selectedIds?: ReadonlySet<string> },
): InterventionTarget[] {
  const out: InterventionTarget[] = [];
  for (const client of clients) {
    for (const quote of client.quotes) {
      const hit = selectedIds
        ? selectedIds.has(quote.id)
        : quote.id === quoteId;
      if (hit)
        out.push({ kind: "quote", clientId: client.id, quoteId: quote.id });
    }
  }
  return out;
}

/** Who is pulling the lever, for attribution. */
export interface Actor {
  name: string;
  /** ISO date the intervention is recorded against. */
  today: string;
}

function attribution(
  actor: Actor,
  reasonCode: string | null,
  note: string,
): RepAttribution {
  return {
    set_by_name: actor.name,
    set_at: actor.today,
    reason_code: reasonCode,
    note,
  };
}

/**
 * Recompute a quote's two money figures from its current share and expected
 * conversion. Mirrors the projection: value is phased by the curve's forward
 * increments unless a rep has pinned a date, in which case it lands there.
 */
function recomputeQuoteMoney(
  quote: PipelineQuote,
  curve: CurvePoint[],
  monthKey: string,
  nextKey: string,
): PipelineQuote {
  const remainingTotal = Math.round(
    quote.quoted_value * quote.conversion_share,
  );

  let inMonth: number;
  if (quote.expected_conversion.origin === "rep") {
    // A pinned date is a point, not a spread: all or nothing for this month.
    const landsIn = monthKeyOf(new Date(quote.expected_conversion.date));
    inMonth = landsIn === monthKey ? remainingTotal : 0;
  } else {
    const landings = curveLandings(curve, quote.age_wd);
    const landingTotal = landings.reduce((s, l) => s + l.share, 0);
    const inMonthShare = landings
      .filter((l) => landingMonth(quote, l.anchorWd) === monthKey)
      .reduce((s, l) => s + l.share, 0);
    const scale = landingTotal > 0 ? quote.conversion_share / landingTotal : 0;
    inMonth = Math.round(quote.quoted_value * inMonthShare * scale);
  }

  return {
    ...quote,
    weighted_remaining_total: remainingTotal,
    weighted_in_month: inMonth,
    weighted_next_month: nextMonthValue(quote, curve, nextKey),
  };
}

/** How much of a quote lands in the month after the response's month. */
function nextMonthValue(
  quote: PipelineQuote,
  curve: CurvePoint[],
  nextKey: string,
): number {
  if (quote.expected_conversion.origin === "rep") {
    const landsIn = monthKeyOf(new Date(quote.expected_conversion.date));
    return landsIn === nextKey
      ? Math.round(quote.quoted_value * quote.conversion_share)
      : 0;
  }
  const landings = curveLandings(curve, quote.age_wd);
  const landingTotal = landings.reduce((s, l) => s + l.share, 0);
  const share = landings
    .filter((l) => landingMonth(quote, l.anchorWd) === nextKey)
    .reduce((s, l) => s + l.share, 0);
  const scale = landingTotal > 0 ? quote.conversion_share / landingTotal : 0;
  return Math.round(quote.quoted_value * share * scale);
}

/**
 * Recompute a quote's hygiene flags after a change. Kept in one place so an
 * intervention cannot leave a stale badge behind — the commonest way a
 * hand-patched UI starts lying.
 */
function recomputeFlags(
  quote: PipelineQuote,
  curve: CurvePoint[],
  today: string,
  staleAgeWd: number,
): PipelineQuote {
  const curveShare = remainingConversion(curve, quote.age_wd);
  const flags: PipelineQuote["flags"] = [];
  if (curveShare <= 0) flags.push("written_off");
  else if (quote.age_wd > staleAgeWd && !quote.reviewed_at)
    flags.push("stale_status");
  if (
    quote.expected_conversion.origin === "rep" &&
    quote.expected_conversion.date < today
  ) {
    flags.push("promised_date_missed");
  }
  return { ...quote, flags };
}

/** Working-day age past which an open quote is treated as needing a decision. */
export const STALE_AGE_WD = 20;

function applyToQuote(
  quote: PipelineQuote,
  intervention: Intervention,
  curve: CurvePoint[],
  actor: Actor,
): PipelineQuote {
  switch (intervention.kind) {
    case "confirm_still_live":
      return { ...quote, reviewed_at: actor.today };

    case "set_conversion":
      return {
        ...quote,
        conversion_share: intervention.share,
        conversion_override: {
          share: intervention.share,
          ...attribution(actor, null, intervention.note),
        },
      };

    case "revert_conversion":
      return {
        ...quote,
        conversion_share: remainingConversion(curve, quote.age_wd),
        conversion_override: null,
      };

    case "set_expected_date": {
      const expected: ExpectedConversion = {
        ...quote.expected_conversion,
        date: intervention.date,
        origin: "rep",
        attribution: attribution(actor, null, intervention.note),
      };
      return { ...quote, expected_conversion: expected };
    }

    case "revert_expected_date": {
      const expected: ExpectedConversion = {
        ...quote.expected_conversion,
        date: quote.expected_conversion.derived_date,
        origin: "derived",
        attribution: null,
      };
      return { ...quote, expected_conversion: expected };
    }

    default:
      return quote;
  }
}

function applyToClient(
  client: PipelineClient,
  intervention: Intervention,
  actor: Actor,
): PipelineClient {
  if (intervention.kind === "set_unseen") {
    return {
      ...client,
      unseen: {
        ...client.unseen,
        adjustment: {
          adjusted_value: intervention.value,
          ...attribution(actor, null, intervention.note),
        },
        effective_value: Math.max(
          0,
          intervention.value - client.unseen.absorbed_value,
        ),
      },
      reviewed_at: actor.today,
    };
  }
  if (intervention.kind === "revert_unseen") {
    return {
      ...client,
      unseen: {
        ...client.unseen,
        adjustment: null,
        effective_value: Math.max(
          0,
          client.unseen.baseline_value - client.unseen.absorbed_value,
        ),
      },
    };
  }
  return client;
}

/** Roll a client's quotes back up into its own totals. */
function recomputeClient(client: PipelineClient): PipelineClient {
  const quotesInMonth = client.quotes.reduce(
    (s, q) => s + q.weighted_in_month,
    0,
  );
  return {
    ...client,
    quotes_in_month: quotesInMonth,
    quotes_next_month: client.quotes.reduce(
      (s, q) => s + q.weighted_next_month,
      0,
    ),
    unseen_in_month: client.unseen.effective_value,
    projected_unbooked: quotesInMonth + client.unseen.effective_value,
    open_quoted_value: client.quotes.reduce((s, q) => s + q.quoted_value, 0),
    written_off_count: client.quotes.filter((q) =>
      q.flags.includes("written_off"),
    ).length,
    flag_count: client.quotes.reduce((s, q) => s + q.flags.length, 0),
  };
}

/** Roll clients back up into the response totals. */
function recomputeResponse(data: MyPipelineResponse): MyPipelineResponse {
  return {
    ...data,
    projected_unbooked: data.clients.reduce(
      (s, c) => s + c.projected_unbooked,
      0,
    ),
    quotes_in_month: data.clients.reduce((s, c) => s + c.quotes_in_month, 0),
    quotes_next_month: data.clients.reduce(
      (s, c) => s + c.quotes_next_month,
      0,
    ),
    unseen_in_month: data.clients.reduce((s, c) => s + c.unseen_in_month, 0),
    open_quoted_value: data.clients.reduce(
      (s, c) => s + c.open_quoted_value,
      0,
    ),
    flag_count: data.clients.reduce((s, c) => s + c.flag_count, 0),
  };
}

/**
 * Apply one intervention and return the whole book, recomputed.
 *
 * Pure: the input is untouched, so the caller can hold the previous response as
 * the undo target and can call this speculatively to preview an effect it has
 * not committed to.
 */
export function applyIntervention(
  data: MyPipelineResponse,
  target: InterventionTarget,
  intervention: Intervention,
  actor: Actor,
): MyPipelineResponse {
  const nextKey = data.next_month;
  const clients = data.clients.map((client) => {
    if (client.id !== target.clientId) return client;

    let updated: PipelineClient;
    if (target.kind === "quote") {
      const quotes: PipelineQuote[] = [];
      for (const quote of client.quotes) {
        if (quote.id !== target.quoteId) {
          quotes.push(quote);
          continue;
        }
        // A lost quote leaves the book: the decision is made and there is
        // nothing left to work, which is exactly what the refetch shows once
        // the server reads the same decision back from the log.
        if (intervention.kind === "mark_lost") continue;
        const next = applyToQuote(quote, intervention, client.curve, actor);
        quotes.push(
          recomputeQuoteMoney(
            recomputeFlags(next, client.curve, actor.today, STALE_AGE_WD),
            client.curve,
            data.month,
            nextKey,
          ),
        );
      }
      updated = { ...client, quotes };
    } else if (target.kind === "arrival") {
      updated = applyArrival(client, data, target.arrivalId, intervention);
    } else {
      updated = applyToClient(client, intervention, actor);
    }

    return recomputeClient(updated);
  });

  const arrivals =
    target.kind === "arrival"
      ? data.arrivals.filter((a) => a.id !== target.arrivalId)
      : data.arrivals;

  return recomputeResponse({ ...data, clients, arrivals });
}

/**
 * Settle one arrival against the projection that predicted it.
 *
 * Absorbing consumes the projection by the arrival's face value — the rep's
 * claim was about work, not about weighted work, so a £19k quote answers £19k
 * of a £45k claim. Any excess beyond what is left is not absorbed: the rep
 * under-called it, and that difference is real new work rather than a reason
 * to push the projection negative.
 *
 * Adding on top leaves the projection alone. That is the deliberate departure,
 * and it is the one that raises the forecast — which is exactly why it is not
 * the default.
 */
function applyArrival(
  client: PipelineClient,
  data: MyPipelineResponse,
  arrivalId: string,
  intervention: Intervention,
): PipelineClient {
  if (intervention.kind !== "resolve_arrival") return client;
  if (intervention.resolution === "add") return client;

  const arrival = data.arrivals.find((a) => a.id === arrivalId);
  if (!arrival) return client;

  const claimed =
    client.unseen.adjustment?.adjusted_value ?? client.unseen.baseline_value;
  const absorbed = Math.min(
    claimed,
    client.unseen.absorbed_value + arrival.value,
  );
  return {
    ...client,
    unseen: {
      ...client.unseen,
      absorbed_value: absorbed,
      effective_value: Math.max(0, claimed - absorbed),
    },
  };
}

/** The month-projection effect of an intervention, without committing it. */
export interface InterventionImpact {
  before: number;
  after: number;
  delta: number;
  /** Delta as a share of the projection before the change. */
  deltaPct: number;
  /** True when this single change alone would trip the material-move alert. */
  isMaterial: boolean;
}

export function previewImpact(
  data: MyPipelineResponse,
  target: InterventionTarget,
  intervention: Intervention,
  actor: Actor,
): InterventionImpact {
  const after = applyIntervention(data, target, intervention, actor);
  return measureMove(data.projected_unbooked, after.projected_unbooked);
}

/** Compare two projection totals — used for one lever and for a whole pass. */
export function measureMove(before: number, after: number): InterventionImpact {
  const delta = after - before;
  const deltaPct = before > 0 ? Math.abs(delta) / before : 0;
  return {
    before,
    after,
    delta,
    deltaPct,
    isMaterial: deltaPct >= MATERIAL_MOVE_PCT,
  };
}

/**
 * The mark-lost route answers 200 with a per-quote outcome, because Tharstern
 * can refuse one estimate and accept the next. A page that ignores the
 * outcomes shows a refused write as saved — which is how an expired estimate
 * looked "lost" on screen while Tharstern still had it open. Returns null when
 * every quote landed, otherwise the sentence to show the rep, carrying
 * Tharstern's own reason where it gave one (the part of `detail` after the
 * error family, e.g. "Estimate cannot be updated as it is Expired").
 */
export function describeMarkLostFailures(
  outcomes: readonly MarkLostOutcome[],
): string | null {
  const failed = outcomes.filter((o) => o.status !== "lost");
  if (failed.length === 0) return null;
  const scope =
    outcomes.length === 1
      ? "This quote was not marked lost"
      : `${failed.length} of ${outcomes.length} quotes were not marked lost`;
  const reasons = Array.from(
    new Set(
      failed.map((o) => {
        const detail = o.detail.trim();
        const stated = detail.includes(": ")
          ? detail.slice(detail.indexOf(": ") + 2)
          : "";
        if (stated) return `Tharstern refused: ${stated}`;
        if (o.status === "write_back_failed")
          return "Tharstern refused the status change";
        return detail ? `not allowed (${detail})` : "not allowed";
      }),
    ),
  );
  return `${scope} — ${reasons.join("; ")}.`;
}

/**
 * A lost row whose note did not reach Tharstern. The status landed, so the
 * quote is decided and stays that way; the rep needs to know the note is only
 * in the CIS log, not on the estimate.
 */
export function describeNoteFailures(
  outcomes: readonly MarkLostOutcome[],
): string | null {
  const affected = outcomes.filter(
    (o) => o.status === "lost" && o.detail.startsWith("NoteNotWritten"),
  );
  if (affected.length === 0) return null;
  const scope =
    outcomes.length === 1
      ? "Marked lost, but the note could not be written to Tharstern"
      : `Marked lost, but the note could not be written to Tharstern on ${affected.length} of ${outcomes.length} quotes`;
  return `${scope}. The note is kept in One.`;
}
